import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';

/**
 * Mobile-friendly picker. Click the field → full-screen sheet with sticky
 * search input + list of options.
 *
 * Props:
 *   value, onChange   — selected option (or null) and setter
 *   placeholder       — text when nothing selected
 *   loadOptions(q)    — async (search:str) => Array<{id, label, sublabel?}>
 *   debounceMs        — debounce on search input (default 200)
 *   labelOf?(opt)     — derive display label from arbitrary opt object
 *   emptyAction?      — { label, onClick } — optional CTA rendered below the
 *                       empty state. Used by the customer pickers to expose
 *                       an "Add new customer" inline-add action when a rep
 *                       can't find who they're looking for. The picker
 *                       closes itself before invoking onClick so the
 *                       caller's navigation runs without the sheet
 *                       lingering on top.
 */
export default function SearchableSelect({
  value,
  onChange,
  placeholder = 'Select…',
  loadOptions,
  debounceMs = 200,
  labelOf = (o) => o.label || o.name,
  emptyAction,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(t);
  }, [query, open, debounceMs]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadOptions(debouncedQuery)
      .then((opts) => {
        if (!cancelled) setOptions(opts);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, open, loadOptions]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full h-10 px-3 border border-gray-300 rounded-md text-body text-left bg-surface hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
      >
        {value ? (
          <span className="text-gray-900">{labelOf(value)}</span>
        ) : (
          <span className="text-gray-400">{placeholder}</span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-canvas flex flex-col">
          {/* Amber header — full-width search bar with the X close
              tucked inside it as a dual-purpose affordance: clears the
              query when there's text, closes the picker when empty.
              Same pattern as iOS UISearchBar. The pt-3 below the safe
              area inset gives a comfortable visual gap between the OS
              status bar and the search bar. */}
          <header className="bg-amber-500 pt-safe-top">
            <div className="px-3 pt-3 pb-3">
              <div className="flex items-center gap-2 px-3 bg-white rounded-card">
                <Search className="h-5 w-5 text-gray-500 shrink-0" aria-hidden="true" />
                <input
                  type="search"
                  autoFocus
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 min-w-0 min-h-touch py-2 bg-transparent text-body text-gray-900 placeholder-gray-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (query) {
                      setQuery('');
                    } else {
                      setOpen(false);
                    }
                  }}
                  aria-label={query ? 'Clear search' : 'Close picker'}
                  className="min-h-touch min-w-touch -mr-2 flex items-center justify-center text-gray-400 active:text-gray-600 shrink-0"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </header>

          {/* Results — list rows in the same mb-card-tap rhythm as the
              rest of the PWA. divide-y keeps separators consistent and
              flush with the canvas edges. */}
          <div className="flex-1 overflow-y-auto bg-canvas">
            {loading && (
              <p className="p-6 text-small text-gray-500 text-center">Loading…</p>
            )}
            {error && (
              <p className="p-6 text-small text-danger text-center">{error}</p>
            )}
            {!loading && !error && options.length === 0 && (
              <div className="p-8 text-center">
                <p className="text-small text-gray-500">
                  No matches{query ? ` for “${query}”` : ''}.
                </p>
                {emptyAction && (
                  <button
                    type="button"
                    onClick={() => {
                      // Close the sheet first so the caller's onClick
                      // (typically a navigate()) lands on a clean stack.
                      setOpen(false);
                      setQuery('');
                      emptyAction.onClick();
                    }}
                    className="mt-4 min-h-touch px-4 py-2 text-small font-medium text-amber-700 active:text-amber-800"
                  >
                    {emptyAction.label}
                  </button>
                )}
              </div>
            )}
            {!loading && !error && options.length > 0 && (
              <ul className="bg-surface divide-y divide-gray-200">
                {options.map((opt) => (
                  <li key={opt.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(opt);
                        setOpen(false);
                        setQuery('');
                      }}
                      className="w-full min-h-touch px-4 py-3 text-left active:bg-gray-50 transition-colors"
                    >
                      <div className="text-body text-gray-900 truncate">
                        {labelOf(opt)}
                      </div>
                      {opt.sublabel && (
                        <div className="text-small text-gray-500 truncate">
                          {opt.sublabel}
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
